"use client";

import type { QuarterlyRow } from "@/lib/types/financial-snapshot";
import { axisLabel, niceScale, CHART_PADDING, CHART_HEIGHT, YAxis, XAxisLabels, ChartSvg } from "../ChartAxes";
import { useChartHover } from "../hover/use-chart-hover";
import { HoverHitAreas } from "../hover/HoverHitAreas";
import { HoverCrosshair } from "../hover/HoverCrosshair";
import { ChartTooltip } from "../hover/ChartTooltip";
import type { HoverPoint } from "../hover/types";

const VIEW_WIDTH = 640;

const fmtMoney = (v: number) => {
  const abs = Math.abs(v);
  if (abs >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(v / 1e6).toFixed(0)}M`;
  return `$${v.toFixed(0)}`;
};
const fmtPct = (v: number) => `${v >= 0 ? "+" : ""}${(v * 100).toFixed(1)}%`;

export function QuarterlyRevenueChart({ rows }: { rows: QuarterlyRow[] }) {
  const { containerRef, activeIndex, handlePointerMove, handlePointerLeave, handlePointerUp } = useChartHover();

  const usable = rows.filter((r) => r.revenue != null);
  if (usable.length === 0) return null;

  const vals = usable.map((r) => r.revenue as number);
  const scale = niceScale(Math.max(...vals, 0), 0);

  const plotTop = CHART_PADDING.top;
  const plotBottom = CHART_HEIGHT - CHART_PADDING.bottom;
  const plotHeight = plotBottom - plotTop;
  const yScale = (v: number) => plotBottom - ((v - scale.min) / (scale.max - scale.min)) * plotHeight;

  const groupWidth = (VIEW_WIDTH - CHART_PADDING.left - CHART_PADDING.right) / usable.length;
  const barWidth = groupWidth * 0.6;
  const xScale = (i: number) => CHART_PADDING.left + i * groupWidth + groupWidth / 2;

  const anchors = usable.map((_, i) => xScale(i));

  const points: HoverPoint[] = usable.map((row) => ({
    period: row.period,
    series: [
      { label: "Revenue", value: row.revenue, format: fmtMoney, colorClass: "bg-blue" },
      { label: "YoY", value: row.yoyPct, format: fmtPct, colorClass: "bg-cgreen" },
    ],
  }));

  return (
    <div ref={containerRef} className="relative">
      <ChartSvg viewBox={`0 0 ${VIEW_WIDTH} ${CHART_HEIGHT}`}>
        <YAxis ticks={scale.ticks} scale={yScale} format={axisLabel} width={VIEW_WIDTH} />
        {usable.map((row, i) => {
          const cx = xScale(i);
          const y = yScale(row.revenue as number);
          return (
            <g key={row.period}>
              <rect
                x={cx - barWidth / 2}
                y={y}
                width={barWidth}
                height={plotBottom - y}
                className="fill-blue"
                rx={2}
              />
              {row.yoyPct != null && (
                <text
                  x={cx}
                  y={y - 4}
                  fontSize={9}
                  textAnchor="middle"
                  className={row.yoyPct >= 0 ? "fill-cgreen" : "fill-cred"}
                >
                  {row.yoyPct >= 0 ? "+" : ""}{(row.yoyPct * 100).toFixed(0)}%
                </text>
              )}
            </g>
          );
        })}
        <XAxisLabels labels={usable.map((r) => r.period)} xScale={xScale} y={CHART_HEIGHT - 8} />
        {activeIndex != null && <HoverCrosshair x={anchors[activeIndex]} plotTop={plotTop} plotBottom={plotBottom} />}
        <HoverHitAreas
          anchors={anchors}
          viewBoxWidth={VIEW_WIDTH}
          plotTop={plotTop}
          plotBottom={plotBottom}
          onActivate={handlePointerMove}
          onLeave={handlePointerLeave}
          onTap={handlePointerUp}
        />
      </ChartSvg>
      {activeIndex != null && (
        <ChartTooltip point={points[activeIndex]} anchorPct={(anchors[activeIndex] / VIEW_WIDTH) * 100} />
      )}
    </div>
  );
}
